// Pure URL helpers for `<OgImage>` (Satori OG rendering). Kept out of
// `og-image.tsx` so that file exports only its React component (Fast Refresh /
// only-export-components). Server-side only — no `'use client'`.

import { ipfsImageUrl } from '@morfi/backend-sdk-v2/server';

const RENDERABLE_EXT = /\.(png|jpe?g)(\?|#|$)/i;

/** Hosts that serve image bytes whose content-type can be PNG, JPEG, OR
 *  WebP regardless of URL extension. Morfi's image proxy stores
 *  covers/avatars under IPFS-CID paths (`/ipfs/<cid>`). Satori (the
 *  renderer behind `next/og`) silently fails to decode WebP and ships the
 *  flat fallback div instead, so these URLs are always re-encoded to JPEG
 *  — see `transformIpfsForSatori`. */
const TRUSTED_IMAGE_HOSTS: readonly string[] = ['images.morfi.markets'];

/** OG canvases are 1200px wide; full-resolution IPFS originals (MOR-2292)
 *  are capped there and re-encoded to JPEG by the gateway. */
const OG_IMAGE_MAX_WIDTH_PX = 1200;
const PARTNER_MEDIA_RE =
  /^https:\/\/morfi-(?:devnet|mainnet)-image-fallback\.s3\.us-east-1\.amazonaws\.com\/partner-media\/[a-z0-9-]+$/i;

export function transformIpfsForSatori(src: string): string {
  // Admin uploads use extensionless object keys and can contain WebP too.
  if (PARTNER_MEDIA_RE.test(src)) {
    return `https://images.weserv.nl/?url=${encodeURIComponent(src)}&output=jpg`;
  }
  return ipfsImageUrl(src, { width: OG_IMAGE_MAX_WIDTH_PX, format: 'jpeg' });
}

/** True when `src` is absolute (https/http/data) and points at a PNG/JPEG. */
export function isOgRenderableSrc(src: string | null | undefined): src is string {
  if (src === null || src === undefined || src === '') return false;
  if (src.startsWith('data:image/png') || src.startsWith('data:image/jpeg')) return true;
  if (!src.startsWith('https://') && !src.startsWith('http://')) return false;
  if (PARTNER_MEDIA_RE.test(src)) return true;
  if (
    TRUSTED_IMAGE_HOSTS.some(
      (h): boolean => src.startsWith(`https://${h}/`) || src.startsWith(`http://${h}/`),
    )
  ) {
    return true;
  }
  return RENDERABLE_EXT.test(src);
}
